// ============================================================================
// src/app/AppEntry.tsx — the application's own top-level routes.
// ============================================================================
// B3.1 finish, last step. The sixteen `view === 'X'` clauses that were still in
// App() — the home dashboard, the cross-program operational surfaces and the
// admin surfaces — are nested <Route> elements here, exactly as the six domain
// entries hold theirs. App() is now gates plus seven top-level route
// dispatches and nothing else.
//
// See src/app/appRoutes.js for the table and why the seven tables partition the
// view space, and src/app/navigation/viewRoutes.js for why `location` is the
// ROUTED location rather than the raw one.
//
// Secondary route leaves are loaded on demand so their code does not delay the
// home screen. Protected routes keep Suspense INSIDE UnauthorizedRedirect, so
// an unauthorized or still-loading caller never triggers the dynamic import.
// Pasture Map retains its original dedicated boundary and loading message.
//
// LightHomePortal is imported here AND in App: App renders it for Light
// containment (any view a Light user may not reach), this module renders it as
// the Light variant of the home route. Two different rules, same component.
// ============================================================================
import React from 'react';
import type {ComponentProps} from 'react';

import {AppRoute, AppRoutes} from './navigation/index.js';
import {APP_ROUTES} from './appRoutes.js';

import UnauthorizedRedirect from '../shared/UnauthorizedRedirect.jsx';
import LightHomePortal from '../dashboard/LightHomePortal.tsx';
import HomeDashboard from '../dashboard/HomeDashboard.tsx';
import PageBoundary from './PageBoundary.tsx';

const PastureMapView = React.lazy(() => import('../pasture/PastureMapView.jsx'));
const AnimalHistoryPage = React.lazy(() => import('../dashboard/AnimalHistoryPage.tsx'));
const ProductionPage = React.lazy(() => import('../dashboard/ProductionPage.tsx'));
const WeatherPage = React.lazy(() => import('../weather/WeatherPage.tsx'));
const MySubmissions = React.lazy(() => import('../dashboard/MySubmissions.tsx'));
const WeighInSessionPage = React.lazy(() => import('../livestock/WeighInSessionPage.tsx'));
const LivestockWeighInsView = React.lazy(() => import('../livestock/LivestockWeighInsView.tsx'));
const EquipmentHome = React.lazy(() => import('../equipment/EquipmentHome.tsx'));
const ProcessingCalendarView = React.lazy(() => import('../processing/ProcessingCalendarView.tsx'));
const ProcessingCutSheetPage = React.lazy(() => import('../processing/ProcessingCutSheetPage.tsx'));
const TaskCenterView = React.lazy(() => import('../tasks/TaskCenterView.tsx'));
const ActivityLogView = React.lazy(() => import('../activity/ActivityLogView.tsx'));
const ProjectsRoute = React.lazy(() => import('../projects/ProjectsRoute.tsx'));
const ClientErrorsView = React.lazy(() => import('../admin/ClientErrorsView.tsx'));
const WebformsAdminView = React.lazy(() => import('../webforms/WebformsAdminView.tsx'));
const NewsletterAdminView = React.lazy(() => import('../newsletter/NewsletterAdminView.tsx'));
const EquipmentAdminRecordPage = React.lazy(() => import('../admin/EquipmentAdminRecordPage.tsx'));

export {APP_ROUTES};

type AppEntryProps = {
  location: ComponentProps<typeof AppRoutes>['location'];
  isLight: boolean;
};

function RouteLoading({label}: {label: string}) {
  return (
    <div role="status" aria-live="polite" style={{padding: '2rem', textAlign: 'center'}}>
      Loading {label}…
    </div>
  );
}

export function AppEntry({location, isLight}: AppEntryProps) {
  const routes = (
    <AppRoutes location={location}>
      {/* Home. Light users get the portal instead of the dashboard.
          canAccessProgram / VIEW_TO_PROGRAM are no longer threaded here: the
          Home launch-tile wall they gated is retired now that the global
          header carries every program destination (and applies the same
          gate). */}
      <AppRoute path="/" element={isLight ? <LightHomePortal /> : <HomeDashboard />} />
      <AppRoute
        path="/animals-on-farm"
        element={
          <React.Suspense fallback={<RouteLoading label="Animal History" />}>
            <AnimalHistoryPage />
          </React.Suspense>
        }
      />
      <AppRoute
        path="/production"
        element={
          <React.Suspense fallback={<RouteLoading label="Production" />}>
            <ProductionPage />
          </React.Suspense>
        }
      />
      <AppRoute
        path="/weather"
        element={
          <React.Suspense fallback={<RouteLoading label="Weather" />}>
            <WeatherPage />
          </React.Suspense>
        }
      />
      {/* View past reports (legacy /my-submissions route). */}
      <AppRoute
        path="/my-submissions"
        element={
          <React.Suspense fallback={<RouteLoading label="Past Submissions" />}>
            <MySubmissions />
          </React.Suspense>
        }
      />
      <AppRoute
        path="/weigh-in-sessions/*"
        element={
          <React.Suspense fallback={<RouteLoading label="Weigh-In Session" />}>
            <WeighInSessionPage />
          </React.Suspense>
        }
      />
      <AppRoute
        path="/broiler/weighins"
        element={
          <React.Suspense fallback={<RouteLoading label="Broiler Weigh-Ins" />}>
            <LivestockWeighInsView species="broiler" />
          </React.Suspense>
        }
      />
      <AppRoute
        path="/pig/weighins"
        element={
          <React.Suspense fallback={<RouteLoading label="Pig Weigh-Ins" />}>
            <LivestockWeighInsView species="pig" />
          </React.Suspense>
        }
      />
      <AppRoute
        path="/fleet/*"
        element={
          <React.Suspense fallback={<RouteLoading label="Fleet" />}>
            <EquipmentHome />
          </React.Suspense>
        }
      />
      {/* Pasture Map (CP1) — auth-gated field surface; not program-scoped.
          farm_team+ can read; management/admin can import/classify/close/delete.
          PastureMapView is React.lazy (the Pasture Map performance boundary);
          Suspense sits
          inside UnauthorizedRedirect so an unauthorized/loading caller never
          triggers the dynamic import at all. */}
      <AppRoute
        path="/pasture-map"
        element={
          <UnauthorizedRedirect requireAdmin={false} fallbackView="home">
            <React.Suspense
              fallback={
                <div role="status" aria-live="polite" style={{padding: '2rem', textAlign: 'center'}}>
                  Loading Pasture Map…
                </div>
              }
            >
              <PastureMapView />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
      {/* Processing calendar — auth-gated cross-program operational surface.
          farm_team/management/admin only (light is bounced by isProcessingRole
          above and never reaches this dispatch; the wrapper handles the
          signed-out case). */}
      <AppRoute
        path="/processing"
        element={
          <UnauthorizedRedirect requireAdmin={false} fallbackView="home">
            <React.Suspense fallback={<RouteLoading label="Processing Calendar" />}>
              <ProcessingCalendarView />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
      {/* Task Center (Tasks v2). /tasks is canonical for every authenticated
          user. Legacy /my-tasks and /admin/tasks redirect here via the URL
          adapter's ALIASES_EXACT map (T11) — the legacy view mounts and their
          imports have been removed. The System Tasks tab self-gates to
          admins inside the view. */}
      <AppRoute
        path="/processing/cut-sheet/*"
        element={
          <UnauthorizedRedirect requireAdmin={false} fallbackView="home">
            <React.Suspense fallback={<RouteLoading label="Cut Sheet" />}>
              <ProcessingCutSheetPage />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
      <AppRoute
        path="/tasks/*"
        element={
          <UnauthorizedRedirect requireAdmin={false} fallbackView="home">
            <React.Suspense fallback={<RouteLoading label="Task Center" />}>
              <TaskCenterView />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
      <AppRoute
        path="/activity"
        element={
          <UnauthorizedRedirect requireAdmin={false} fallbackView="home">
            <React.Suspense fallback={<RouteLoading label="Activity Log" />}>
              <ActivityLogView />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
      {/* Projects (owner decisions D1-D17): every active role. */}
      <AppRoute
        path="/projects"
        element={
          <UnauthorizedRedirect requireAdmin={false} fallbackView="home">
            <React.Suspense fallback={<RouteLoading label="Projects" />}>
              <ProjectsRoute />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
      {/* Client errors — admin-only runtime observability. */}
      <AppRoute
        path="/admin/client-errors"
        element={
          <UnauthorizedRedirect requireAdmin={true} fallbackView="home">
            <React.Suspense fallback={<RouteLoading label="Client Errors" />}>
              <ClientErrorsView />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
      {/* Webforms admin (admin-only). Route-level guard, not just the hidden
          Header nav button: a non-admin who types/bookmarks /admin must never
          see the admin config surface. Mirrors the clientErrors gate. */}
      <AppRoute
        path="/admin"
        element={
          <UnauthorizedRedirect requireAdmin={true} fallbackView="home">
            <React.Suspense fallback={<RouteLoading label="Admin Settings" />}>
              <WebformsAdminView />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
      {/* Newsletter admin (admin-only). Mirrors the clientErrors/webforms
          gates: a non-admin who types/bookmarks /admin/newsletter is bounced
          home. The public archive at /newsletter is a separate, no-login
          surface handled above the auth gates. */}
      <AppRoute
        path="/admin/newsletter"
        element={
          <UnauthorizedRedirect requireAdmin={true} fallbackView="home">
            <React.Suspense fallback={<RouteLoading label="Newsletter Admin" />}>
              <NewsletterAdminView />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
      {/* A40: one Admin record page per piece of equipment (admin-only).
          Mirrors the /admin gate: the editor it hosts is the same admin
          config surface, so a non-admin who types/bookmarks the address
          gets No Access, never the editor. */}
      <AppRoute
        path="/admin/equipment/*"
        element={
          <UnauthorizedRedirect requireAdmin={true} fallbackView="home">
            <React.Suspense fallback={<RouteLoading label="Equipment" />}>
              <EquipmentAdminRecordPage />
            </React.Suspense>
          </UnauthorizedRedirect>
        }
      />
    </AppRoutes>
  );
  // N12: a crash in the routed page stays inside it; App and the header
  // stay mounted (src/app/PageBoundary).
  return <PageBoundary>{routes}</PageBoundary>;
}

export default AppEntry;
